import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { Database, AppUser } from '../types/database'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as
  | string
  | undefined
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as
  | string
  | undefined
export const APP_URL =
  ((import.meta as any).env?.VITE_APP_URL as string) ||
  (typeof window !== 'undefined' ? window.location.origin : '')

export const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY)

export const supabase: SupabaseClient<Database> | null = isSupabaseConfigured
  ? createClient<Database>(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null

if (!isSupabaseConfigured && typeof window !== 'undefined') {
  console.warn('[Growthly] Supabase env vars missing — running in MOCK mode.')
}

/* =====================================================================
   ACCESS TOKEN SYSTEM
   The anon key alone cannot mint a session from a custom token, so we
   delegate validation to the Edge Function `access-token-login`, which
   uses the service role to mint a magic-link URL we then navigate to.
   ===================================================================== */

/**
 * Look up a user by access_token. Returns `null` if not found.
 * Note: requires a public-read policy on users.access_token, OR call this
 * from the edge function. Here we expose it for client-side preview only.
 */
export async function validateAccessToken(
  token: string,
): Promise<AppUser | null> {
  if (!isSupabaseConfigured || !supabase) return null
  if (!token || !token.startsWith('grw_')) return null

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('access_token', token)
    .maybeSingle()

  if (error) {
    console.error('[validateAccessToken]', error)
    return null
  }
  return (data as AppUser) ?? null
}

/**
 * Exchanges an access_token for a Supabase session.
 *
 * Calls the Edge Function `access-token-login` which:
 *   1. Validates the token server-side (service role)
 *   2. Mints a one-time magic-link URL
 *   3. Returns { magic_link } — we navigate to it; Supabase finishes the
 *      login via detectSessionInUrl.
 */
export async function signInWithAccessToken(
  token: string,
): Promise<{ ok: boolean; error?: string }> {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, error: 'Supabase not configured' }
  }
  if (!token?.startsWith('grw_')) {
    return { ok: false, error: 'Token inválido' }
  }

  const { data, error } = await supabase.functions.invoke(
    'access-token-login',
    {
      body: { token },
    },
  )

  if (error) return { ok: false, error: error.message }
  if (!data?.magic_link)
    return { ok: false, error: 'No se recibió enlace de acceso' }

  // Navigate to the magic link — Supabase will pick up the session
  window.location.href = data.magic_link
  return { ok: true }
}
